/**
 * Archivio ZIP costruito nel browser.
 *
 * Dopo il pagamento i documenti sono gia' in memoria (r.blob /
 * r.content_base64): impacchettarli qui evita di rimandarli al server e
 * riscaricarli, e soprattutto non dipende dalla sessione Stripe, che puo'
 * mancare - era il motivo per cui "scarica tutti" ripiegava sui download
 * singoli.
 *
 * I file vengono memorizzati senza compressione (metodo 0, "stored"). I PDF
 * sono gia' compressi al loro interno: sgonfiarli di nuovo costerebbe CPU per
 * qualche punto percentuale. Cosi' l'archivio si costruisce in un passaggio
 * solo sui byte.
 *
 * Nessuna dipendenza esterna: il formato usato e' il sottoinsieme classico
 * (header locale + directory centrale + coda), senza ZIP64. Sopra i 4 GB
 * complessivi servirebbe ZIP64, ma un lotto di documenti non ci arriva.
 */

const TABELLA_CRC = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) c = TABELLA_CRC[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** Data e ora nel formato DOS usato dallo ZIP. */
function dataDos(d) {
  const ora = ((d.getHours() & 31) << 11) | ((d.getMinutes() & 63) << 5) | ((d.getSeconds() / 2) & 31);
  const data = (((d.getFullYear() - 1980) & 127) << 9) | (((d.getMonth() + 1) & 15) << 5) | (d.getDate() & 31);
  return { ora, data };
}

/**
 * Restituisce i byte grezzi. `creaZip` li avvolge in un Blob: separarli
 * serve a poterli verificare nei test senza dipendere dall'implementazione di
 * Blob dell'ambiente.
 *
 * @param {{nome: string, dati: Uint8Array}[]} voci
 * @param {Date} [quando]
 * @returns {Uint8Array}
 */
export function creaZipBytes(voci, quando = new Date()) {
  const { ora, data } = dataDos(quando);
  const enc = new TextEncoder();
  const pezzi = [];
  const centrale = [];
  let offset = 0;

  for (const { nome, dati } of voci) {
    const nomeBytes = enc.encode(nome);
    const crc = crc32(dati);

    const locale = new Uint8Array(30 + nomeBytes.length);
    const vl = new DataView(locale.buffer);
    vl.setUint32(0, 0x04034b50, true);
    vl.setUint16(4, 20, true);        // versione necessaria
    vl.setUint16(6, 0x0800, true);    // nome in UTF-8
    vl.setUint16(8, 0, true);         // metodo: stored
    vl.setUint16(10, ora, true);
    vl.setUint16(12, data, true);
    vl.setUint32(14, crc, true);
    vl.setUint32(18, dati.length, true);
    vl.setUint32(22, dati.length, true);
    vl.setUint16(26, nomeBytes.length, true);
    vl.setUint16(28, 0, true);        // extra
    locale.set(nomeBytes, 30);

    pezzi.push(locale, dati);

    const cd = new Uint8Array(46 + nomeBytes.length);
    const vc = new DataView(cd.buffer);
    vc.setUint32(0, 0x02014b50, true);
    vc.setUint16(4, 20, true);        // versione di chi ha scritto
    vc.setUint16(6, 20, true);        // versione necessaria
    vc.setUint16(8, 0x0800, true);
    vc.setUint16(10, 0, true);
    vc.setUint16(12, ora, true);
    vc.setUint16(14, data, true);
    vc.setUint32(16, crc, true);
    vc.setUint32(20, dati.length, true);
    vc.setUint32(24, dati.length, true);
    vc.setUint16(28, nomeBytes.length, true);
    vc.setUint16(30, 0, true);        // extra
    vc.setUint16(32, 0, true);        // commento
    vc.setUint16(34, 0, true);        // disco
    vc.setUint16(36, 0, true);        // attributi interni
    vc.setUint32(38, 0, true);        // attributi esterni
    vc.setUint32(42, offset, true);   // posizione dell'header locale
    cd.set(nomeBytes, 46);
    centrale.push(cd);

    offset += locale.length + dati.length;
  }

  const dimCentrale = centrale.reduce((s, c) => s + c.length, 0);
  const coda = new Uint8Array(22);
  const vf = new DataView(coda.buffer);
  vf.setUint32(0, 0x06054b50, true);
  vf.setUint16(4, 0, true);
  vf.setUint16(6, 0, true);
  vf.setUint16(8, voci.length, true);
  vf.setUint16(10, voci.length, true);
  vf.setUint32(12, dimCentrale, true);
  vf.setUint32(16, offset, true);
  vf.setUint16(20, 0, true);

  const parti = [...pezzi, ...centrale, coda];
  const totale = parti.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(totale);
  let o = 0;
  for (const p of parti) { out.set(p, o); o += p.length; }
  return out;
}

/** Come creaZipBytes, ma pronto da salvare su disco. */
export function creaZip(voci, quando = new Date()) {
  return new Blob([creaZipBytes(voci, quando)], { type: "application/zip" });
}

/**
 * Rende unico un nome dentro l'archivio.
 *
 * Due documenti possono chiamarsi uguale - lo stesso "referto.pdf" da
 * cartelle diverse - e in uno ZIP il secondo sovrascriverebbe il primo in
 * silenzio: l'utente aprirebbe l'archivio e troverebbe meno file di quanti
 * ne ha pagati.
 */
export function nomeUnico(nome, usati) {
  if (!usati.has(nome)) { usati.add(nome); return nome; }
  const punto = nome.lastIndexOf(".");
  const radice = punto > 0 ? nome.slice(0, punto) : nome;
  const est = punto > 0 ? nome.slice(punto) : "";
  let n = 2;
  while (usati.has(`${radice} (${n})${est}`)) n += 1;
  const finale = `${radice} (${n})${est}`;
  usati.add(finale);
  return finale;
}
